import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalFooter } from "@/components/LegalFooter";

/**
 * HINWEIS: Erstfassung der Datenschutzerklärung für Xenomons. Deckt
 * Verantwortlichen, verarbeitete Daten, Auftragsverarbeiter (inkl. KI-
 * Gateway), Rechte und Account-Löschung ab. Vor Produktiv-Launch
 * juristisch prüfen lassen.
 */
export const Route = createFileRoute("/datenschutz")({
  head: () => ({
    meta: [
      { title: "Datenschutzerklärung – Xenomons" },
      {
        name: "description",
        content:
          "Wie Xenomons personenbezogene Daten verarbeitet: Account, Spielstand, KI-Generierung, Auftragsverarbeiter und deine Rechte.",
      },
      { property: "og:title", content: "Datenschutzerklärung – Xenomons" },
      {
        property: "og:description",
        content: "Datenschutzhinweise zu Xenomons und der Schmetti GmbH.",
      },
      { property: "og:url", content: "https://xenomons.online/datenschutz" },
    ],
    links: [
      { rel: "canonical", href: "https://xenomons.online/datenschutz" },
      { rel: "alternate", hrefLang: "de", href: "https://xenomons.online/datenschutz" },
      { rel: "alternate", hrefLang: "en", href: "https://xenomons.online/datenschutz" },
      { rel: "alternate", hrefLang: "x-default", href: "https://xenomons.online/datenschutz" },
    ],
  }),
  component: Datenschutz,
});

const EMAIL = "webmaster@xenomons.online";

function MailLink() {
  return (
    <a className="underline" href={`mailto:${EMAIL}`}>
      {EMAIL}
    </a>
  );
}

function RichItem({ html }: { html: string }) {
  // Render simple <b>...</b> markup inline without dangerouslySetInnerHTML.
  const parts = html.split(/(<b>.*?<\/b>)/g);
  return (
    <>
      {parts.map((part, i) => {
        const m = part.match(/^<b>(.*?)<\/b>$/);
        if (m) return <strong key={i}>{m[1]}</strong>;
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function Datenschutz() {
  const { t } = useTranslation("legal");
  const s2 = t("privacy.s2_items", { returnObjects: true }) as string[];
  const s3 = t("privacy.s3_items", { returnObjects: true }) as string[];
  const s4 = t("privacy.s4_items", { returnObjects: true }) as string[];

  return (
    <main className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 px-6 py-10">
        <div className="mx-auto max-w-2xl">
          <Link to="/" className="text-sm font-medium text-primary hover:underline">
            {t("back")}
          </Link>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight">
            {t("privacy.title")}
          </h1>

          <p className="mt-3 text-xs italic text-muted-foreground">
            {t("privacy.status")}
          </p>

          <section className="mt-6 space-y-5 text-sm leading-relaxed">
            <div>
              <h2 className="text-lg font-bold">{t("privacy.s1_title")}</h2>
              <p className="whitespace-pre-line">
                {t("privacy.s1_body")} <MailLink />
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold">{t("privacy.s2_title")}</h2>
              <ul className="list-disc pl-5 space-y-1">
                {s2.map((item, i) => (
                  <li key={i}><RichItem html={item} /></li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-bold">{t("privacy.s3_title")}</h2>
              <ul className="list-disc pl-5 space-y-1">
                {s3.map((item, i) => (
                  <li key={i}><RichItem html={item} /></li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-bold">{t("privacy.s4_title")}</h2>
              <ul className="list-disc pl-5 space-y-1">
                {s4.map((item, i) => (
                  <li key={i}><RichItem html={item} /></li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-2">
                {t("privacy.s4_note")}
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold">{t("privacy.s5_title")}</h2>
              <p>{t("privacy.s5_body")}</p>
            </div>

            <div>
              <h2 className="text-lg font-bold">{t("privacy.s6_title")}</h2>
              <p>
                {t("privacy.s6_body_1")} <MailLink />
                {t("privacy.s6_body_2")}
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold">{t("privacy.s7_title")}</h2>
              <p>
                {t("privacy.s7_body_1")} <MailLink />
                {t("privacy.s7_body_2")}
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold">{t("privacy.s8_title")}</h2>
              <p>
                {t("privacy.s8_body_1")} <MailLink />
                {t("privacy.s8_body_2")}
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold">{t("privacy.s9_title")}</h2>
              <p>{t("privacy.s9_body")}</p>
            </div>
          </section>
        </div>
      </div>
      <LegalFooter />
    </main>
  );
}
